import React, { useState, useEffect, useCallback } from 'react';
import {
  UmbrellaOff,
  Check,
  X as XIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Users,
  CalendarOff,
} from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import toast from 'react-hot-toast';

import api from '../api/axios';
import { PersonName } from '../components/common/PersonColor';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import VacationRangePicker from '../components/common/VacationRangePicker';
import {
  AdminRequestActions, DeleteVacationModal, EditVacationModal, VacationChangeResultModal, PersonRequestsCard,
} from '../components/vacation/AdminVacationTools';
import {
  MAX_VACATION_DAYS, rangeEndStr, formatVacationRange, rangesOverlap, daysInYear,
} from '../constants/vacationRange';

// ── Helpers ───────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const styles = {
    approved: { color: '#166534', background: '#dcfce7', border: '1px solid #bbf7d0' },
    rejected: { color: '#991b1b', background: '#fee2e2', border: '1px solid #fecaca' },
    pending:  { color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a' },
  };
  const labels = { approved: 'Approved', rejected: 'Rejected', pending: 'Pending' };
  return (
    <span style={{
      ...styles[status],
      borderRadius: '4px',
      padding: '2px 8px',
      fontWeight: 600,
      fontSize: '0.78rem',
      whiteSpace: 'nowrap',
    }}>
      {labels[status] || status}
    </span>
  );
}

// Group vacations by month (YYYY-MM)
function groupByMonth(vacations) {
  const groups = {};
  for (const v of vacations) {
    const month = v.start_date.slice(0, 7);
    if (!groups[month]) groups[month] = [];
    groups[month].push(v);
  }
  return groups;
}

// ── BalanceBar ────────────────────────────────────────────────────────────────

function BalanceBar({ balance }) {
  const { total = MAX_VACATION_DAYS, approved = 0, pending = 0, remaining = MAX_VACATION_DAYS } = balance;
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.88rem' }}>
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>Annual allowance</span>
        <span style={{ color: remaining === 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
          <strong style={{ color: remaining === 0 ? 'var(--danger)' : 'var(--text)' }}>{remaining}</strong>
          {' '}of {total} days remaining
        </span>
      </div>
      <div style={{
        height: '8px',
        borderRadius: '4px',
        background: 'var(--border)',
        overflow: 'hidden',
        display: 'flex',
      }}>
        <div style={{ flex: approved, background: '#22c55e', transition: 'flex 0.3s' }} />
        <div style={{ flex: pending, background: '#f59e0b', transition: 'flex 0.3s' }} />
        <div style={{ flex: Math.max(0, remaining), background: 'transparent' }} />
      </div>
      <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        <span><span style={{ color: '#22c55e', fontWeight: 700 }}>■</span> Approved: {approved}</span>
        {pending > 0 && <span><span style={{ color: '#f59e0b', fontWeight: 700 }}>■</span> Pending: {pending}</span>}
        <span style={{ marginLeft: 'auto' }}>Total: {total} days/year</span>
      </div>
    </div>
  );
}

// ── VacationRequestModal ──────────────────────────────────────────────────────

const todayStr = () => format(new Date(), 'yyyy-MM-dd');

// Client-side hints only — the server checks overlap and the 28-day limit per year
// Admins additionally choose the person ("Request for") and may approve immediately
function VacationRequestModal({
  isOpen, onClose, onSubmit, activeRequests, balance, year, allowPast, saving,
  isAdmin, users = [], currentUserId,
}) {
  const [entry, setEntry] = useState({ start_date: '', length_days: 7 });
  const [targetId, setTargetId] = useState(currentUserId);
  const [approveNow, setApproveNow] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEntry({ start_date: '', length_days: 7 });
      setTargetId(currentUserId);
      setApproveNow(false);
    }
  }, [isOpen, currentUserId]);

  const forSelf = !isAdmin || Number(targetId) === currentUserId;

  const minDate = allowPast ? null : format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const endDate = entry.start_date ? rangeEndStr(entry.start_date, entry.length_days) : null;
  const range = endDate ? { start_date: entry.start_date, end_date: endDate } : null;
  // Own requests/balance are known here; for someone else the server checks
  const clash = forSelf && range && activeRequests.find((r) => rangesOverlap(r, range));
  const pastStart = range && !allowPast && entry.start_date <= todayStr();
  // Remaining days are known for the displayed year only
  const daysThisYear = range ? daysInYear(range.start_date, range.end_date, year) : 0;
  const overQuota = forSelf && daysThisYear > 0 && daysThisYear > balance.remaining;
  const blocked = !range || !!clash || pastStart;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Request Vacation" size="md">
      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '14px' }}>
        Vacation is taken in whole weeks (7, 14, 21 or 28 days) and can start on any day.
        {forSelf
          ? ` You have ${balance.remaining} of ${balance.total ?? MAX_VACATION_DAYS} days left in ${year} (approved + pending).`
          : ` The 28-day limit per year and overlaps are checked for the selected person.`}
        {allowPast && ' As an admin you can also enter past dates.'}
      </p>

      {isAdmin && (
        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label className="form-label" htmlFor="vacation-for">Request for</label>
          <select
            id="vacation-for"
            className="form-input"
            value={targetId ?? ''}
            disabled={saving}
            onChange={(e) => setTargetId(Number(e.target.value))}
          >
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}{u.id === currentUserId ? ' (me)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <VacationRangePicker
        startDate={entry.start_date}
        lengthDays={entry.length_days}
        min={minDate}
        onChange={setEntry}
        disabled={saving}
      />

      {clash && (
        <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: 'var(--radius)', background: 'var(--danger-light)', color: 'var(--danger)', fontSize: '0.83rem' }}>
          Overlaps your {clash.status} vacation {formatVacationRange(clash.start_date, clash.end_date)}.
        </div>
      )}
      {pastStart && (
        <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: 'var(--radius)', background: 'var(--danger-light)', color: 'var(--danger)', fontSize: '0.83rem' }}>
          Vacation must start in the future.
        </div>
      )}
      {!clash && overQuota && (
        <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: 'var(--radius)', background: 'var(--warning-light)', color: '#b45309', fontSize: '0.83rem' }}>
          This uses {daysThisYear} days in {year}, but only {balance.remaining} are left. The request will be refused.
        </div>
      )}

      {isAdmin && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', fontSize: '0.88rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={approveNow}
            disabled={saving}
            onChange={(e) => setApproveNow(e.target.checked)}
          />
          Approve immediately
        </label>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={() => !blocked && onSubmit(entry, isAdmin ? { user_id: Number(targetId), approve: approveNow } : {})}
          disabled={blocked || saving}
        >
          {saving ? 'Submitting…' : approveNow ? 'Save approved vacation' : 'Request Vacation'}
        </button>
      </div>
    </Modal>
  );
}

// ── VacationPage ──────────────────────────────────────────────────────────────

export default function VacationPage() {
  const { isAdmin, user } = useAuth();
  const [userOptions, setUserOptions] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [myData, setMyData] = useState({
    requests: [],
    balance: { total: MAX_VACATION_DAYS, approved: 0, pending: 0, remaining: MAX_VACATION_DAYS },
  });
  const [teamApproved, setTeamApproved] = useState([]);
  const [pendingAll, setPendingAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actioning, setActioning] = useState(null);
  // Admin delete / edit
  const [deleteReq, setDeleteReq] = useState(null);
  const [editReq, setEditReq] = useState(null);
  const [changeResult, setChangeResult] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadAll = useCallback(() => {
    setLoading(true);
    const fetches = [
      api.get(`/vacation-requests/me?year=${year}`),
      api.get(`/vacation-requests/approved?year=${year}`),
    ];
    if (isAdmin) {
      fetches.push(api.get('/vacation-requests?status=pending'));
    }
    Promise.all(fetches.map((p) => p.catch(() => null))).then(([meRes, approvedRes, pendingRes]) => {
      if (meRes) setMyData(meRes.data);
      if (approvedRes) setTeamApproved(approvedRes.data);
      if (pendingRes) setPendingAll(pendingRes.data);
      setLoading(false);
    });
  }, [year, isAdmin]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Own requests come without user_name
  const withName = (req) => ({ ...req, user_name: req.user_name || user?.name || 'this person' });
  const openEdit = (req) => setEditReq(withName(req));
  const openDelete = (req) => setDeleteReq(withName(req));

  function afterAdminChange(data, req) {
    setDeleteReq(null);
    setEditReq(null);
    loadAll();
    setReloadKey((k) => k + 1);
    if (data?.conflicts?.length || data?.draft_plans?.length) {
      setChangeResult({ ...data, personName: req.user_name });
    }
  }

  // Admins can enter vacation for any active user
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/users')
      .then((res) => setUserOptions((res.data || [])
        .filter((u) => u.is_active)
        .sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
  }, [isAdmin]);

  async function handleSubmit(entry, adminOpts = {}) {
    setSaving(true);
    try {
      await api.post('/vacation-requests', { start_date: entry.start_date, length_days: entry.length_days, ...adminOpts });
      const other = adminOpts.user_id && adminOpts.user_id !== user?.id
        ? userOptions.find((u) => u.id === adminOpts.user_id)?.name
        : null;
      toast.success(other
        ? `Vacation ${adminOpts.approve ? 'approved' : 'requested'} for ${other}.`
        : adminOpts.approve ? 'Vacation saved and approved.' : 'Vacation request submitted.');
      setWeekPickerOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to submit.');
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove(id) {
    setActioning(id);
    try {
      await api.put(`/vacation-requests/${id}/approve`);
      toast.success('Vacation approved.');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve.');
    } finally {
      setActioning(null);
    }
  }

  async function handleReject(id) {
    setActioning(id);
    try {
      await api.put(`/vacation-requests/${id}/reject`);
      toast.success('Vacation request rejected.');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reject.');
    } finally {
      setActioning(null);
    }
  }

  const activeRequests = myData.requests.filter((r) => r.status !== 'rejected');

  const monthGroups = groupByMonth(teamApproved);
  const currentYear = new Date().getFullYear();

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Vacation</h1>
          <p className="page-subtitle">
            {isAdmin
              ? 'Review requests and see team vacation schedule.'
              : 'Request vacation and view team availability.'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Year selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 8px' }}
              onClick={() => setYear((y) => y - 1)}
              title="Previous year"
            >
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontWeight: 600, minWidth: '46px', textAlign: 'center', fontSize: '0.95rem' }}>
              {year}
            </span>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 8px' }}
              onClick={() => setYear((y) => y + 1)}
              title="Next year"
            >
              <ChevronRight size={14} />
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => setWeekPickerOpen(true)}>
            <UmbrellaOff size={15} />
            Request Vacation
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Admin: pending requests */}
          {isAdmin && pendingAll.length > 0 && (
            <div className="card" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Clock size={17} style={{ color: '#f59e0b' }} />
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Pending Requests</h2>
                <span style={{
                  background: '#f59e0b',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '1px 8px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}>
                  {pendingAll.length}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pendingAll.map((req) => (
                  <div
                    key={req.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      background: '#fffbeb',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.92rem' }}><PersonName userId={req.user_id} name={req.user_name} /></div>
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {formatVacationRange(req.start_date, req.end_date)}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      Requested {format(parseISO(req.requested_at), 'MMM d')}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        className="btn btn-primary"
                        style={{
                          padding: '5px 12px',
                          fontSize: '0.82rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: '#22c55e',
                          borderColor: '#22c55e',
                        }}
                        onClick={() => handleApprove(req.id)}
                        disabled={actioning === req.id}
                      >
                        <Check size={13} />
                        Approve
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{
                          padding: '5px 12px',
                          fontSize: '0.82rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          color: 'var(--danger)',
                          borderColor: 'var(--danger)',
                        }}
                        onClick={() => handleReject(req.id)}
                        disabled={actioning === req.id}
                      >
                        <XIcon size={13} />
                        Reject
                      </button>
                    </div>
                    <AdminRequestActions req={req} onEdit={openEdit} onDelete={openDelete} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {isAdmin && pendingAll.length === 0 && (
            <div
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                fontSize: '0.85rem',
                color: '#166534',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Check size={15} />
              No pending vacation requests.
            </div>
          )}

          {isAdmin && (
            <PersonRequestsCard
              users={userOptions}
              year={year}
              reloadKey={reloadKey}
              onEdit={openEdit}
              onDelete={openDelete}
              StatusBadge={StatusBadge}
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '20px', alignItems: 'start' }}>
            {/* Left: My Vacation */}
            <div className="card">
              <h2 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 700 }}>
                My Vacation {year !== currentYear && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{year}</span>}
              </h2>
              <BalanceBar balance={myData.balance} />

              {myData.requests.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '28px 16px',
                  color: 'var(--text-muted)',
                }}>
                  <CalendarOff size={32} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.4 }} />
                  <p style={{ fontSize: '0.88rem', margin: 0 }}>No vacation requests for {year}.</p>
                  {year >= currentYear && (
                    <button
                      className="btn btn-secondary"
                      style={{ marginTop: '12px', fontSize: '0.82rem' }}
                      onClick={() => setWeekPickerOpen(true)}
                    >
                      Request your first vacation
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {myData.requests.map((req) => (
                    <div
                      key={req.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        flexWrap: 'wrap',
                      }}
                    >
                      <UmbrellaOff
                        size={14}
                        style={{
                          flexShrink: 0,
                          color: req.status === 'approved'
                            ? '#22c55e'
                            : req.status === 'rejected'
                            ? 'var(--danger)'
                            : '#f59e0b',
                        }}
                      />
                      <div style={{ flex: '1 1 180px' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.87rem' }}>
                          {formatVacationRange(req.start_date, req.end_date)}
                        </div>
                        {req.decided_by_name && req.decided_at && (
                          <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', marginTop: '1px' }}>
                            {req.status === 'approved' ? 'Approved' : 'Rejected'} by {req.decided_by_name}
                            {' · '}{format(parseISO(req.decided_at), 'MMM d')}
                          </div>
                        )}
                      </div>
                      <StatusBadge status={req.status} />
                      {isAdmin && <AdminRequestActions req={req} onEdit={openEdit} onDelete={openDelete} />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Team Vacations */}
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Users size={16} style={{ color: 'var(--primary)' }} />
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
                  Team Vacations {year !== currentYear && year}
                </h2>
                {teamApproved.length > 0 && (
                  <span style={{
                    background: 'var(--primary)',
                    color: 'white',
                    borderRadius: '10px',
                    padding: '1px 7px',
                    fontSize: '0.73rem',
                    fontWeight: 700,
                  }}>
                    {teamApproved.length}
                  </span>
                )}
              </div>

              {teamApproved.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '28px 16px', color: 'var(--text-muted)' }}>
                  <UmbrellaOff size={32} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.3 }} />
                  <p style={{ fontSize: '0.88rem', margin: 0 }}>No approved vacations for {year}.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  {Object.entries(monthGroups).map(([month, vacations]) => (
                    <div key={month}>
                      <div style={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: 'var(--text-muted)',
                        marginBottom: '6px',
                        borderBottom: '1px solid var(--border)',
                        paddingBottom: '4px',
                      }}>
                        {format(parseISO(month + '-01'), 'MMMM yyyy')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {vacations.map((v) => (
                          <div
                            key={v.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '9px',
                              padding: '7px 10px',
                              borderRadius: '6px',
                              background: 'rgba(124, 58, 237, 0.07)',
                              border: '1px solid rgba(124, 58, 237, 0.18)',
                              flexWrap: 'wrap',
                            }}
                          >
                            <UmbrellaOff size={13} style={{ color: '#7c3aed', flexShrink: 0 }} />
                            <div style={{ flex: '1 1 180px', fontSize: '0.85rem' }}>
                              <PersonName userId={v.user_id} name={v.user_name} style={{ fontWeight: 700 }} />
                              <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>
                                {formatVacationRange(v.start_date, v.end_date)}
                              </span>
                            </div>
                            {isAdmin && <AdminRequestActions req={v} onEdit={openEdit} onDelete={openDelete} />}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <VacationRequestModal
        isOpen={weekPickerOpen}
        onClose={() => setWeekPickerOpen(false)}
        onSubmit={handleSubmit}
        activeRequests={activeRequests}
        balance={myData.balance}
        year={year}
        allowPast={isAdmin}
        saving={saving}
        isAdmin={isAdmin}
        users={userOptions.length ? userOptions : (user ? [{ id: user.id, name: user.name }] : [])}
        currentUserId={user?.id}
      />

      {isAdmin && (
        <>
          <DeleteVacationModal req={deleteReq} onClose={() => setDeleteReq(null)} onDone={afterAdminChange} />
          <EditVacationModal req={editReq} onClose={() => setEditReq(null)} onDone={afterAdminChange} />
          <VacationChangeResultModal result={changeResult} onClose={() => setChangeResult(null)} />
        </>
      )}
    </div>
  );
}
